import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import Header from '@/components/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExportData = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState({
    members: false,
    advertisers: false,
    bills: false,
    dispatches: false
  });

  const exportData = async (module) => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to export data');
      navigate('/login');
      return;
    }

    setLoading({ ...loading, [module]: true });

    try {
      const response = await axios.get(`${API}/${module}/export/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data.data;
      const count = response.data.count;

      if (count === 0) {
        toast.info(`No ${module} data to export`);
        setLoading({ ...loading, [module]: false });
        return;
      }

      // Convert to CSV
      const headers = Object.keys(data[0]);
      const csvContent = [
        headers.join(','),
        ...data.map(item => 
          headers.map(header => {
            const value = item[header];
            // Handle values with commas or quotes
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',')
        )
      ].join('\n');

      // Download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `${module}_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      URL.revokeObjectURL(url);
      
      toast.success(`Exported ${count} ${module} records successfully!`);
    } catch (error) {
      console.error(`Error exporting ${module}:`, error);
      if (error.response?.status === 403 || error.response?.status === 401) {
        toast.error('Admin access required');
      } else {
        toast.error(`Failed to export ${module} data`);
      }
    } finally {
      setLoading({ ...loading, [module]: false });
    }
  };

  const exportOptions = [
    {
      title: 'Members',
      description: 'Export all member records with complete data',
      module: 'members',
      color: '#1e40af'
    },
    {
      title: 'Advertisers',
      description: 'Export all advertiser records with complete data',
      module: 'advertisers',
      color: '#059669'
    },
    {
      title: 'Bills',
      description: 'Export all billing records with complete data',
      module: 'bills',
      color: '#d97706'
    },
    {
      title: 'Dispatches',
      description: 'Export all dispatch records with complete data',
      module: 'dispatches',
      color: '#7c3aed'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-red-50 py-8">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center space-x-4 mb-8">
          <Button variant="outline" onClick={() => navigate('/')} data-testid="back-button">
            <ArrowLeft className="mr-2" size={18} />
            Back
          </Button>
          <h1 className="text-4xl font-bold" style={{ color: '#dc2626' }} data-testid="export-title">
            Export All Data
          </h1>
        </div>

        <div className="form-section mb-8">
          <p className="text-gray-700 mb-6">
            Export complete database tables for backup purposes. All fields including created_at, updated_at, created_by, and updated_by will be included.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {exportOptions.map((option) => (
              <div
                key={option.module}
                className="border rounded-lg p-6 hover:shadow-lg transition-shadow"
                style={{ borderColor: option.color }}
              >
                <h3 className="text-xl font-semibold mb-2" style={{ color: option.color }}>
                  {option.title}
                </h3>
                <p className="text-gray-600 mb-4">{option.description}</p>
                <Button
                  onClick={() => exportData(option.module)}
                  disabled={loading[option.module]}
                  style={{ backgroundColor: option.color }}
                  className="w-full"
                  data-testid={`export-${option.module}-button`}
                >
                  <Download className="mr-2" size={18} />
                  {loading[option.module] ? 'Exporting...' : `Export ${option.title}`}
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <h2 className="text-xl font-semibold mb-4" style={{ color: '#dc2626' }}>
            Export Information
          </h2>
          <ul className="list-disc list-inside space-y-2 text-gray-700">
            <li>Exported files are in CSV format compatible with Excel and other spreadsheet applications</li>
            <li>All records and fields are included in the export</li>
            <li>Files are named with the current date for easy identification</li>
            <li>Only administrators can export data</li>
            <li>Use these exports for backup, reporting, or data migration purposes</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ExportData;
